import { Duration, OrientationRatio, type AspectRatioValues, type Resolution } from '@/config/model-params'
import { SceneEngine, SceneID } from '@/enums/agent'
import type {
  GenerationMode,
  ModelConfigItem,
  ModelTypeOption,
  OptionWithCredits
} from '@/lib/api/models/config'
import { isPixelWxHResolution, matchResolutionOption } from '@/lib/utils/resolution'

/**
 * 是否走 Standard / Reference 双模式分支（完全由 admin supportReference 决定）
 */
export const usesDualMode = (model?: ModelConfigItem | null): boolean =>
  Boolean(model?.supportReference)

/**
 * 当前生成模式应提交的 engine：
 * Reference 且配置了 referenceEngine 时用副引擎，否则用主 engine
 */
export const getActiveEngine = (
  model: ModelConfigItem | null | undefined,
  generationMode: GenerationMode
): string => {
  if (generationMode === 'reference' && model?.referenceEngine) {
    return model.referenceEngine
  }
  return model?.engine ?? ''
}

/**
 * Reference 编辑器是否仅允许图片（无音/视频上传）
 */
export const isImageOnlyReference = (model?: ModelConfigItem | null): boolean => {
  if (!model?.supportReference) return false
  const refMode = model.referenceMode
  const supportAudio = refMode?.supportAudioUpload ?? model.supportAudioUpload ?? false
  const supportVideo = refMode?.supportVideoUpload ?? model.supportVideoUpload ?? false
  return !supportAudio && !supportVideo
}

/**
 * 默认生成模式：优先 admin 字段；过渡期兼容 HappyHorse → reference
 */
export const getDefaultGenerationMode = (model?: ModelConfigItem | null): GenerationMode => {
  if (!model) return 'standard'
  if (model.defaultGenerationMode === 'standard' || model.defaultGenerationMode === 'reference') {
    return model.defaultGenerationMode
  }
  // 过渡兼容：旧 HappyHorse 未下发字段时默认 Reference
  if (
    model.engine === SceneEngine.HappyHorse ||
    model.sceneId === SceneID.HAPPYHORSE ||
    model.name?.toLowerCase().includes('happyhorse')
  ) {
    return 'reference'
  }
  return 'standard'
}

/**
 * 提交 model_type：优先 admin；过渡期按 Seedance sceneId 推断
 */
export const getModelType = (model?: ModelConfigItem | null): ModelTypeOption => {
  if (!model) return 'standard'
  if (model.modelType === 'standard' || model.modelType === 'fast' || model.modelType === 'mini') {
    return model.modelType
  }
  if (model.sceneId === SceneID.SEEDANCE2_FAST) return 'fast'
  if (model.sceneId === SceneID.SEEDANCE2_MINI) return 'mini'
  return 'standard'
}

/**
 * Standard 有图时是否锁定比例
 */
export const shouldLockRatioWhenHasImages = (model?: ModelConfigItem | null): boolean => {
  if (!model) return false
  if (typeof model.lockRatioWhenHasImages === 'boolean') {
    return model.lockRatioWhenHasImages
  }
  // 过渡兼容：HappyHorse
  return (
    model.engine === SceneEngine.HappyHorse ||
    model.sceneId === SceneID.HAPPYHORSE ||
    model.name?.toLowerCase().includes('happyhorse')
  )
}

/** MiniMax H3 sceneId；Ref 未下发 resolutionOptions 时的兜底过滤目标 */
const MINIMAX_H3_SCENE_ID = '60'

/** RM3.5 后台下发 3/5/8 秒，但 Ref 模式不支持 8 秒 */
export const RM3_5_REF_UNSUPPORTED_DURATION = Duration.DURATION_8S

export const isRm35Model = (model?: ModelConfigItem | null): boolean =>
  String(model?.sceneId ?? '') === SceneID.RM3_5_MODEL

/** Real Motion 3.5 Fast（admin 仍下发 auto，提交需转成 adaptive） */
export const isRm35FastModel = (
  model?: Pick<ModelConfigItem, 'engine'> | null
): boolean => String(model?.engine ?? '') === SceneEngine.RM3_5_FAST

/**
 * 提交用 aspect_ratio_v2：3.5 Fast 选 Auto 时传 adaptive，其余原样
 */
export const toSubmitAspectRatioV2 = (
  ratio?: string | null,
  model?: Pick<ModelConfigItem, 'engine'> | null
): AspectRatioValues => {
  const value = (ratio || OrientationRatio.RATIO_AUTO) as AspectRatioValues
  const isAuto =
    value === OrientationRatio.RATIO_AUTO || value === OrientationRatio.RATIO_ADAPTIVE
  if (isAuto && isRm35FastModel(model)) {
    return OrientationRatio.RATIO_ADAPTIVE
  }
  return value
}

const KNOWN_ASPECT_RATIOS = new Set<string>(Object.values(OrientationRatio))

/**
 * Adjust 回填用：把提交值还原成输入框比例（adaptive → auto）
 */
export const fromSubmitAspectRatioV2 = (
  ratio?: string | null
): AspectRatioValues | undefined => {
  if (!ratio) return undefined
  if (ratio === OrientationRatio.RATIO_ADAPTIVE) {
    return OrientationRatio.RATIO_AUTO
  }
  if (KNOWN_ASPECT_RATIOS.has(ratio)) {
    return ratio as AspectRatioValues
  }
  return undefined
}

/**
 * Adjust 回填比例：优先 aspect_ratio_v2，其次导演台 ratio，最后才用 portrait/landscape
 */
export const resolveRestoredOrientation = ({
  aspectRatioV2,
  directorRatio,
  aspectRatio
}: {
  aspectRatioV2?: string | null
  directorRatio?: string | null
  aspectRatio?: string | null
}): AspectRatioValues => {
  return (
    fromSubmitAspectRatioV2(aspectRatioV2) ??
    fromSubmitAspectRatioV2(directorRatio) ??
    (aspectRatio === 'landscape' ? OrientationRatio.RATIO_16_9 : OrientationRatio.RATIO_9_16)
  )
}

export const isRm35RefUnsupportedDuration = (duration?: string | null): boolean =>
  String(duration ?? '') === RM3_5_REF_UNSUPPORTED_DURATION

/** RM3.5：Ref 模式下禁用 8 秒 */
export const isRm35DurationDisabledInReference = (
  model: ModelConfigItem | null | undefined,
  isReference: boolean,
  duration: string
): boolean => isRm35Model(model) && isReference && isRm35RefUnsupportedDuration(duration)

/** RM3.5：已选 8 秒时禁用 Ref 模式 */
export const isRm35ReferenceDisabledForDuration = (
  model: ModelConfigItem | null | undefined,
  duration?: string | null
): boolean => isRm35Model(model) && isRm35RefUnsupportedDuration(duration)

/** RM3.5 Ref 下 8 秒的回退时长：优先 5 秒，否则第一个非 8 秒 */
export const getRm35ReferenceFallbackDuration = (
  model: ModelConfigItem | null | undefined
): Duration | undefined => {
  const allowed = (model?.durationOptions ?? []).filter(
    option => !isRm35RefUnsupportedDuration(String(option.value))
  )
  return (
    allowed.find(option => String(option.value) === Duration.DURATION_5S)?.value ??
    allowed.find(option => option.isDefault)?.value ??
    allowed[0]?.value
  )
}

/**
 * 当前生成模式下可用的分辨率列表：
 * 1. Reference 且 admin 下发了 referenceMode.resolutionOptions → 用 REF 列表
 * 2. MiniMax H3（60）Reference 未下发时 → 从 STA 列表排除 宽x高（480），保留 768P/2K
 * 3. 否则 → 顶层 STA 列表
 */
export const getResolutionOptions = (
  model: ModelConfigItem | null | undefined,
  generationMode: GenerationMode
): OptionWithCredits<Resolution, string>[] => {
  if (generationMode === 'reference') {
    const refOptions = model?.referenceMode?.resolutionOptions
    if (refOptions && refOptions.length > 0) {
      return refOptions
    }

    // H3：admin 尚未下发 Ref 列表时，Ref 仅支持非像素档（768P / 2K）
    if (String(model?.sceneId ?? '') === MINIMAX_H3_SCENE_ID) {
      return (model?.resolutionOptions ?? []).filter(o => !isPixelWxHResolution(String(o.value)))
    }
  }
  return model?.resolutionOptions ?? []
}

/** 从分辨率列表取默认值（isDefault 优先，否则第一项） */
export const getDefaultResolutionFromOptions = (
  options: OptionWithCredits<Resolution, string>[]
): string | undefined => options.find(r => r.isDefault)?.value ?? options[0]?.value

/**
 * 若当前分辨率不在可用列表中，钳位到列表默认项；已合法则原样返回
 */
export const clampResolutionToOptions = (
  current: string,
  options: OptionWithCredits<Resolution, string>[]
): string | undefined => {
  if (!options.length) return undefined
  const matched = matchResolutionOption(current, options)
  if (matched) return matched.value
  return getDefaultResolutionFromOptions(options)
}
